import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Users, 
  Trophy, 
  Wallet, 
  Calendar, 
  ChevronRight,
  Star,
  Zap
} from 'lucide-react';

const LOGO_URL = process.env.REACT_APP_LOGO_URL;
const HERO_IMAGE = process.env.REACT_APP_HERO_IMAGE;
const COMMUNITY_IMAGE = process.env.REACT_APP_COMMUNITY_IMAGE;
const FIELD_IMAGE = process.env.REACT_APP_FIELD_IMAGE;

const features = [
  {
    icon: Calendar,
    title: 'Spiele erstellen',
    description: 'Erstelle Spiele und lade Spieler aus deiner Community ein.',
  },
  {
    icon: MapPin,
    title: 'Felder finden',
    description: 'Entdecke die besten Fußballfelder in Tunis auf unserer interaktiven Karte.',
  },
  {
    icon: Trophy,
    title: 'ELO-Rangliste',
    description: 'Verbessere deinen ELO-Rang und klettere an die Spitze der Rangliste.',
  },
  {
    icon: Wallet,
    title: 'Sichere Bezahlung',
    description: 'Bezahle sicher mit D17, Flouci oder Bankkarte in TND.',
  },
];

const stats = [
  { value: '500+', label: 'Aktive Spieler' },
  { value: '50+', label: 'Fußballfelder' },
  { value: '1000+', label: 'Gespielte Spiele' },
  { value: '4.8', label: 'Bewertung' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="landing-logo">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-8 w-8 sm:h-10 sm:w-10 rounded-full object-cover" />
              <span className="font-display font-bold text-lg sm:text-xl text-tunis-blue">Tunis Kick</span>
            </Link>
            <div className="flex items-center gap-1 sm:gap-3">
              <Link to="/community">
                <Button variant="ghost" className="font-semibold text-tunis-blue hidden sm:inline-flex">
                  Community
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="ghost" className="font-semibold text-tunis-blue hidden sm:inline-flex">
                  Über uns
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-tunis-blue text-sm sm:text-base px-2 sm:px-4" data-testid="landing-login">
                  Anmelden
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-3 sm:px-6 text-sm sm:text-base" data-testid="landing-register">
                  Jetzt starten
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-12 sm:pt-24 sm:pb-16 md:pt-32 md:pb-24 hex-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="space-y-6 sm:space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-tunis-teal/10 text-tunis-teal rounded-full text-xs sm:text-sm font-semibold">
                <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
                Die #1 Fußball-App in Tunis
              </div>
              
              <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-tunis-blue leading-tight tracking-tight">
                SPIEL.
                <br />
                <span className="text-tunis-teal">VERBINDE.</span>
                <br />
                GEWINNE.
              </h1>
              
              <p className="text-base sm:text-lg text-muted-foreground max-w-lg">
                Die ultimative Plattform für Amateur-Fußball in Tunis. 
                Finde Spiele, verbinde dich mit Spielern und buche die besten Felder der Stadt.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to="/register" className="w-full sm:w-auto">
                  <Button size="lg" className="btn-primary w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg" data-testid="hero-cta">
                    Kostenlos starten
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/map" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="btn-secondary w-full sm:w-auto px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg" data-testid="hero-map">
                    Karte ansehen
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 pt-6 sm:pt-8 border-t border-slate-200">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center sm:text-left">
                    <div className="font-display font-bold text-xl sm:text-2xl text-tunis-blue">{stat.value}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Image - Hidden on very small screens */}
            <div className="relative animate-slide-up stagger-2 hidden sm:block">
              <div className="absolute inset-0 bg-gradient-to-br from-tunis-teal/20 to-tunis-blue/20 rounded-3xl transform rotate-3"></div>
              <img
                src={HERO_IMAGE}
                alt="Fußball Action"
                className="relative rounded-3xl shadow-floating object-cover w-full h-[300px] md:h-[400px] lg:h-[500px]"
              />
              {/* Floating Card */}
              <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-white rounded-xl md:rounded-2xl shadow-floating p-3 md:p-4 animate-fade-in stagger-3">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-tunis-teal/10 rounded-lg md:rounded-xl flex items-center justify-center">
                    <Users className="w-5 h-5 md:w-6 md:h-6 text-tunis-teal" />
                  </div>
                  <div>
                    <div className="font-bold text-tunis-blue text-sm md:text-base">+50 Spieler</div>
                    <div className="text-xs md:text-sm text-muted-foreground">diese Woche beigetreten</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-tunis-blue mb-3 sm:mb-4">
              Alles was du brauchst
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Von der Spielerstellung bis zur Bezahlung - wir haben alles abgedeckt.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1"
                  data-testid={`feature-${index}`}
                >
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-tunis-teal/10 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
                    <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-tunis-teal" />
                  </div>
                  <h3 className="font-display font-bold text-sm sm:text-lg text-tunis-blue mb-1 sm:mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm line-clamp-3">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img
                src={COMMUNITY_IMAGE}
                alt="Fußball Community"
                className="rounded-3xl shadow-floating object-cover w-full h-[400px]"
              />
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <h2 className="font-display font-bold text-3xl md:text-4xl text-tunis-blue">
                Werde Teil der Community
              </h2>
              <p className="text-lg text-muted-foreground">
                Verbinde dich mit Hunderten von Spielern in Tunis. Egal ob Anfänger oder 
                erfahrener Spieler - hier findest du das perfekte Match für dein Niveau.
              </p>
              <ul className="space-y-4">
                {[
                  'Spiele mit Spielern auf deinem Niveau',
                  'Verbessere deinen ELO-Rang',
                  'Trete Teams bei oder gründe dein eigenes',
                  'Gewinne Auszeichnungen und Belohnungen'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-tunis-teal rounded-full flex items-center justify-center">
                      <Star className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-tunis-blue font-medium">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/register">
                  <Button className="btn-primary px-8" data-testid="community-cta">
                    Jetzt beitreten
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 bg-tunis-blue">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4 sm:mb-6">
            Bereit zum Spielen?
          </h2>
          <p className="text-base sm:text-lg text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Erstelle dein kostenloses Konto und finde noch heute dein erstes Spiel in Tunis.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-tunis-teal hover:bg-tunis-teal/90 text-white font-bold px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-full w-full sm:w-auto" data-testid="final-cta">
              Kostenlos registrieren
            </Button>
          </Link>
        </div>
      </section>

      {/* Sponsors Section */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm font-semibold text-tunis-teal uppercase tracking-wider mb-2">Unsere Partner & Sponsoren</p>
            <h2 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-tunis-blue">
              Unterstützt von den Besten
            </h2>
          </div>
          
          {/* Main Sponsor - Anis Ben Hatira */}
          <div className="mb-8 sm:mb-12">
            <div className="bg-gradient-to-r from-tunis-blue to-tunis-blue/90 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-xl">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 rounded-lg sm:rounded-xl overflow-hidden border-2 sm:border-3 border-white/20 shadow-lg">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_3a696a4c-81d4-434e-a061-9984ef8c7b19/artifacts/2zyz6w2s_Anis%20ben%20hatira.jpeg"
                      alt="Anis Ben Hatira"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-amber-400 text-amber-900 text-[10px] sm:text-xs font-bold rounded-full">HAUPTSPONSOR</span>
                  </div>
                  <h3 className="font-display font-bold text-lg sm:text-xl md:text-2xl text-white mb-1">Anis Ben Hatira</h3>
                  <p className="text-white/70 text-xs sm:text-sm mb-2 sm:mb-3">Profifußballer</p>
                  <p className="text-white/80 text-xs sm:text-sm md:text-base max-w-xl">
                    "Jeder, der Fußball liebt, verdient schöne Momente auf dem Platz – egal wo auf der Welt."
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Other Sponsors Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {/* Sponsor 1 - Placeholder */}
            <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-slate-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-lg flex items-center justify-center mb-2 sm:mb-3">
                <span className="text-xl sm:text-2xl">⚽</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-slate-700">Partner werden</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Kontaktiere uns</p>
            </div>
            
            {/* Sponsor 2 - Placeholder */}
            <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-slate-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-lg flex items-center justify-center mb-2 sm:mb-3">
                <span className="text-xl sm:text-2xl">🏢</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-slate-700">Partner werden</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Kontaktiere uns</p>
            </div>
            
            {/* Sponsor 3 - Placeholder */}
            <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-slate-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-lg flex items-center justify-center mb-2 sm:mb-3">
                <span className="text-xl sm:text-2xl">⚽</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-slate-700">Partner werden</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Kontaktiere uns</p>
            </div>
            
            {/* Sponsor 4 - Placeholder */}
            <div className="bg-slate-50 rounded-lg sm:rounded-xl p-4 sm:p-6 flex flex-col items-center justify-center hover:shadow-lg transition-shadow border border-slate-200">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-lg flex items-center justify-center mb-2 sm:mb-3">
                <span className="text-xl sm:text-2xl">🌍</span>
              </div>
              <p className="text-xs sm:text-sm font-semibold text-slate-700">Partner werden</p>
              <p className="text-[10px] sm:text-xs text-slate-500">Kontaktiere uns</p>
            </div>
          </div>
          
          {/* Become a Sponsor CTA */}
          <div className="mt-8 sm:mt-10 text-center">
            <p className="text-slate-600 mb-3 sm:mb-4 text-sm sm:text-base">Möchten Sie Tunis Kick unterstützen?</p>
            <Link to="/about">
              <Button variant="outline" className="border-tunis-blue text-tunis-blue hover:bg-tunis-blue hover:text-white text-sm sm:text-base">
                Sponsor werden
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-10 w-10 rounded-full object-cover" />
              <span className="font-display font-bold text-xl">Tunis Kick</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="text-white/60 hover:text-white transition-colors">
                Über uns
              </Link>
              <span className="text-white/60">© 2024 Tunis Kick</span>
              <span className="text-white/60">Made with ❤️ in Tunis</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
